import { useState } from 'react'

import { WorkspaceDTO } from 'features/workspace'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { GamSetupOrder } from 'features/workspace/types/gamSetupOrder'
import { DEFAULT_REFETCH_INTERVAL } from 'providers/tanstack-query/constants'
import { useQuery } from 'providers/tanstack-query/useQuery'
import { gql } from 'providers/tanstack-query/utils'
import { GamSetupOrderSliceDTO } from './useGetGamSetupOrders/api/types'

export type GamSetupOrderFreshStatusAndError = Pick<GamSetupOrder, 'errorMessage' | 'id' | 'status'>
export type GetFreshStatusAndError = (
  id?: GamSetupOrder['id'],
) => GamSetupOrderFreshStatusAndError | null

type Data = {
  node: null | {
    gamSetupOrders: Pick<GamSetupOrderSliceDTO, 'errorMessage' | 'id' | 'status'>[]
  }
}
type Variables = {
  workspaceId: WorkspaceDTO['id']
}

export const useGetFreshStatusAndErrorForGamSetupOrders = (): {
  getFreshStatusAndError: GetFreshStatusAndError
} => {
  const { workspaceId } = useWorkspaceParam()
  const [result, setResult] = useState<GamSetupOrderFreshStatusAndError[] | null>(null)

  useQuery<Variables, Data>({
    onCompleted: async data => {
      if (data?.node?.gamSetupOrders !== undefined) {
        setResult(
          data.node.gamSetupOrders.map(
            ({ errorMessage, id, status }): GamSetupOrderFreshStatusAndError => ({
              errorMessage: errorMessage || undefined,
              id,
              status,
            }),
          ),
        )
      }
    },
    options: {
      refetchInterval: DEFAULT_REFETCH_INTERVAL,
    },
    query: gql`
      query getFreshStatusAndErrorForGamSetupOrders($workspaceId: ID!) {
        node(id: $workspaceId) {
          ... on Workspace {
            gamSetupOrders {
              errorMessage
              id
              status
            }
          }
        }
      }
    `,
    variables: { workspaceId },
  })

  const getFreshStatusAndError: GetFreshStatusAndError = id => {
    if (id === undefined || result === null) {
      return null
    }

    return result.find((el: GamSetupOrderFreshStatusAndError): boolean => el.id === id) ?? null
  }

  return {
    getFreshStatusAndError,
  }
}
